import { colors } from "./colors";
import type { RouteColorName } from "../../../types/routeBuilder";

/** Route Builder options and generated-route colors share the palette keys. */
export const ROUTE_COLOR_NAMES = Object.keys(colors) as RouteColorName[];

/** First palette color not in use; once every color is taken, the least-used one. */
export function leastUsedRouteColor(
  usedColors: RouteColorName[],
): RouteColorName {
  const counts = new Map<RouteColorName, number>();
  for (const color of usedColors) {
    counts.set(color, (counts.get(color) ?? 0) + 1);
  }
  return ROUTE_COLOR_NAMES.reduce((least, color) =>
    (counts.get(color) ?? 0) < (counts.get(least) ?? 0) ? color : least,
  );
}

const FALLBACK_PALETTE = colors.blue;

/** Retired palette keys still stored on routes — map to the nearest active color. */
const LEGACY_ROUTE_COLOR_ALIASES: Record<string, RouteColorName> = {
  stone: "gray",
  zinc: "gray",
};

export function isNamedRouteColor(color: string | null | undefined): boolean {
  if (!color) return false;
  if (Object.prototype.hasOwnProperty.call(colors, color)) return true;
  return Object.prototype.hasOwnProperty.call(
    LEGACY_ROUTE_COLOR_ALIASES,
    color,
  );
}

/** Keep Route Builder colors within the named palette. */
export function normalizeRouteColor(
  color: string | null | undefined,
): RouteColorName {
  if (color && Object.prototype.hasOwnProperty.call(colors, color)) {
    return color as RouteColorName;
  }
  if (color && LEGACY_ROUTE_COLOR_ALIASES[color]) {
    return LEGACY_ROUTE_COLOR_ALIASES[color];
  }
  return "blue";
}

/** Tailwind class tokens for a named route color. */
export function resolveRouteColorPalette(color: string | null | undefined) {
  return colors[normalizeRouteColor(color)] ?? FALLBACK_PALETTE;
}
